// Property Maintenance Section - Maintenance schedule
import React from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet } from 'react-native';
import { FormSection } from '@/components/common/admin/form';
import { ADMIN_COLORS } from '@/constants/admin';

export type DayOfWeek = 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday' | 'Sunday';

export interface MaintenanceDay {
  enabled: boolean;
  startTime: string;
  endTime: string;
}

interface PropertyMaintenanceSectionProps {
  maintenanceSchedule: Record<DayOfWeek, MaintenanceDay>;
  onDayToggle: (day: DayOfWeek) => void;
  onTimeChange: (day: DayOfWeek, field: 'start' | 'end', value: string) => void;
  disabled?: boolean;
}

const DAYS: DayOfWeek[] = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export const PropertyMaintenanceSection: React.FC<PropertyMaintenanceSectionProps> = ({
  maintenanceSchedule,
  onDayToggle,
  onTimeChange,
  disabled,
}) => {
  return (
    <FormSection title="Maintenance Schedule" subtitle="Set available hours for maintenance requests">
      {DAYS.map((day) => (
        <View key={day} style={styles.maintenanceDay}>
          <TouchableOpacity
            style={styles.checkboxRow}
            onPress={() => onDayToggle(day)}
            activeOpacity={0.7}
            disabled={disabled}
          >
            <View style={[styles.checkbox, maintenanceSchedule[day].enabled && styles.checkboxChecked]}>
              {maintenanceSchedule[day].enabled && (
                <View style={styles.checkboxInner} />
              )}
            </View>
            <Text style={styles.dayLabel}>{day}</Text>
          </TouchableOpacity>

          {maintenanceSchedule[day].enabled && (
            <View style={styles.timePickersRow}>
              <View style={styles.timePicker}>
                <Text style={styles.timeLabel}>Start Time</Text>
                <TextInput
                  style={styles.timeInput}
                  value={maintenanceSchedule[day].startTime}
                  onChangeText={(value) => onTimeChange(day, 'start', value)}
                  placeholder="09:00"
                  placeholderTextColor={ADMIN_COLORS.TEXT_TERTIARY}
                  editable={!disabled}
                />
              </View>

              <View style={styles.timePicker}>
                <Text style={styles.timeLabel}>End Time</Text>
                <TextInput
                  style={styles.timeInput}
                  value={maintenanceSchedule[day].endTime}
                  onChangeText={(value) => onTimeChange(day, 'end', value)}
                  placeholder="17:00"
                  placeholderTextColor={ADMIN_COLORS.TEXT_TERTIARY}
                  editable={!disabled}
                />
              </View>
            </View>
          )}
        </View>
      ))}
    </FormSection>
  );
};

const styles = StyleSheet.create({
  maintenanceDay: {
    marginBottom: 20,
  },
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderWidth: 2,
    borderColor: ADMIN_COLORS.BORDER,
    borderRadius: 4,
    marginRight: 12,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
  },
  checkboxChecked: {
    borderColor: ADMIN_COLORS.BUTTON.ADD_BUTTON_BACKGROUND,
    backgroundColor: ADMIN_COLORS.BUTTON.ADD_BUTTON_BACKGROUND,
  },
  checkboxInner: {
    width: 10,
    height: 10,
    backgroundColor: '#FFFFFF',
    borderRadius: 2,
  },
  dayLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT.PRIMARY,
  },
  timePickersRow: {
    flexDirection: 'row',
    gap: 12,
    marginLeft: 32,
  },
  timePicker: {
    flex: 1,
  },
  timeLabel: {
    fontSize: 12,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT.SECONDARY,
    marginBottom: 6,
  },
  timeInput: {
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    borderRadius: 8,
    padding: 10,
    fontSize: 14,
    color: ADMIN_COLORS.TEXT.PRIMARY,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
  },
});

